import { Box, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Paper, useTheme, useMediaQuery } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { ButtonStato } from '../../../commons/styleds/buttons';
import { SelectStato } from '../../../commons/styleds/selects';
import {
  IComandaEtapaReunioes,
  IReuniaoInterna,
  StatusReuniaoEnum,
  StatusValidacaoEnum,
} from '../../../commons/types/crm';
import { useStatusActions } from '../../../states/comandas/status/Status.actions';
import { useReuniaoActions } from '../../../states/reunioes/reuniao.actions';
import { useReuniaoInternaActions } from '../../../states/reunioesInternas/reunioesInternas.actions';
import TimesheetRestultado from './Components/resultado';
import { useTranslation } from '../../../commons/useTranslasion';
import { useRecoilValue } from 'recoil';
import { authAtom } from '../../../states/usuarios/AuthState';
import { useTimesheetConsultorActions } from '../../../states/timesheet/timesheetConsultor.actions';
import { themeConstants } from '../../../commons/theme';
import { useDateFormatters } from '../../../utils/useDateFormatters';

enum periodoEnum {
  MES_ATUAL = 'Mês Atual',
  MES_PASSADO = 'Mês Passado',
  MES_RETRASADO = 'Mês Retrasado',
  RANGE_DATAS = 'Range de Datas',
}

function formatarCamelCase(str: string) {
  return str
    .normalize("NFD") // Remove acentos
    .replace(/[\u0300-\u036f]/g, "") // Remove diacríticos
    .replace(/[^a-zA-Z0-9 ]/g, "") // Remove caracteres especiais
    .trim() // Remove espaços extras do início e fim
    .split(/\s+/) // Divide em palavras
    .map((palavra: string , index: any ) => {
      if (index === 0) return palavra.toLowerCase();
      return palavra.charAt(0).toUpperCase() + palavra.slice(1).toLowerCase();
    })
    .join('');
}


const RelatorioTimeSheet: React.FC = () => {
  const { translate } = useTranslation();
  const { formatoDataDayjs, localeDayjs } = useDateFormatters();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const auth = useRecoilValue(authAtom);
  const reunioesActions = useReuniaoActions();
  const reunioesInternasActions = useReuniaoInternaActions();
  const timesheetConsultorActions = useTimesheetConsultorActions();
  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState<'filtrar' | 'exportar' | null>(null);
  const [dataReady, setDataReady] = useState(false);
  const [renderComplete, setRenderComplete] = useState(false);
  const [dataInicio, setDataInicio] = useState<Date | null>(null);
  const [dataFim, setDataFim] = useState<Date | null>(null);
  const [periodo, setPeriodo] = useState<string>('RANGE_DATAS');
  const [comandaEtapasReunioes, setComandaEtapasReunioes] = useState<IComandaEtapaReunioes[]>([]);
  const [reunioesInternas, setReunioesInternas] = useState<IReuniaoInterna[]>([]);

  const onChangePeriodo = (valor: string) => {
    setPeriodo(valor);

    if (valor === 'RANGE_DATAS') {
      setDataInicio(null);
      setDataFim(null);
      return;
    }

    const now = new Date();
    switch (valor) {
      case 'MES_ATUAL':
        setDataInicio(new Date(now.getFullYear(), now.getMonth(), 1));
        setDataFim(new Date(now.getFullYear(), now.getMonth() + 1, 0));
        break;
      case 'MES_PASSADO':
        setDataInicio(new Date(now.getFullYear(), now.getMonth() - 1, 1));
        setDataFim(new Date(now.getFullYear(), now.getMonth(), 0));
        break;
      case 'MES_RETRASADO':
        setDataInicio(new Date(now.getFullYear(), now.getMonth() - 2, 1));
        setDataFim(new Date(now.getFullYear(), now.getMonth() - 1, 0));
        break;
      default:
        break;
    }
  };

  const validaPodeEnviar = () => {
    if (periodo === 'RANGE_DATAS' && (!dataInicio || !dataFim)) {
      toast.warn(translate('eNecessarioPreencherAsDuasDatas'));
      return false;
    }

    return true;
  };

  const getDataBusca = () => {
    let data = {
      dataInicio: dataInicio ? dataInicio.toISOString() : '',
      dataFim: dataFim ? dataFim.toISOString() : '',
      consultorId: auth?.id,
    };

    return data;
  };

  const filtrarRelatorio = (e: any) => {
    e.preventDefault();
    if (!validaPodeEnviar()) return;
    
    setLoading(true);
    setLoadingAction('filtrar');

    const data = getDataBusca() as any;
    
    data.statusReuniao = [
      StatusReuniaoEnum.APROVADA, 
      StatusReuniaoEnum.PENDENTE_APROVACAO
    ];

    const params = new URLSearchParams();
    
    Object.entries(data)
      .filter(([_, value]) => value !== null && value !== undefined)
      .forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(item => {
            params.append(key, item.toString());
          });
        } else {
          params.append(key, value!.toString());
        }
      });
    
    const queryString = params.toString();

    reunioesActions
      .listTimeSheet(`?${queryString}`)
      .then((resp: any) => {
        setComandaEtapasReunioes(resp.resultado);
        // Primeira parte dos dados carregada
        setDataReady(prev => resp.resultado.length > 0 || prev);
      })
      .catch((e) => toast.error(e));

    reunioesInternasActions
      .list(false, `?${queryString}`)
      .then((resp: any) => {
        setReunioesInternas(resp);
        // Segunda parte dos dados carregada
        setDataReady(prev => resp.length > 0 || prev);
      })
      .catch((e) => toast.error(e))
      .finally(() => {
        // Se não houver dados para mostrar, desativamos o loading imediatamente
        if (!(dataReady || comandaEtapasReunioes.length > 0 || reunioesInternas.length > 0)) {
          setLoading(false);
          setLoadingAction(null);
        }
      });
  };

  const gerarRelatorio = (e: any) => {
    e.preventDefault();
    if (!validaPodeEnviar()) return;
    setLoading(true);
    setLoadingAction('exportar');

    const data = getDataBusca();

    timesheetConsultorActions
      .create(data)
      .then((resp: any) => {
        toast.success(translate('relatorioGerado'));
        window.open(resp.linkDownload, '_blank', 'noopener,noreferrer');
      })
      .catch((e: any) => toast.error(e))
      .finally(() => {
        setLoading(false);
        setLoadingAction(null);
      });
  };

  // Função que será chamada quando a tabela for renderizada
  const handleRenderComplete = () => {
    setRenderComplete(true);
  };

  // Efeito para garantir que o loading só desapareça quando a tabela for completamente renderizada
  useEffect(() => {
    if (renderComplete && loading && loadingAction === 'filtrar') {
      // A tabela foi renderizada, podemos desativar o loading
      const timer = setTimeout(() => {
        setLoading(false);
        setLoadingAction(null);
        setDataReady(false);
        setRenderComplete(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [renderComplete, loading, loadingAction]);
  
  return (
    <>
      <DivConteudo>
        <Grid container item xs={12}>
          <h2 style={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }}>{translate('preenchaAsInformacoesParaEstrairORelatorio')}</h2>
        </Grid>

        <form style={{ width: '100%' }}>
          <PaperRelatorio elevation={3}>
            <GridTimeSheet container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth sx={{ mb: isMobile ? 1 : 0 }}>
                  <InputLabel id="demo-simple-select-label">{translate('periodo')}</InputLabel>
                  <SelectStato
                    value={periodo}
                    onChange={(e) => onChangePeriodo(e.target.value as string)}
                    required
                    label={translate('periodo')}
                    sx={{ width: '100%' }}
                  >
                    {Object.keys(periodoEnum).map((periodo: string, index: number) => (
                      <MenuItem value={periodo} key={index}>
                        {translate(formatarCamelCase(periodoEnum[periodo as keyof typeof periodoEnum]))}
                      </MenuItem>
                    ))}
                  </SelectStato>
                </FormControl>
              </Grid>

                  <Grid item xs={12} md={4}>
                {periodo === 'RANGE_DATAS' && (
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={localeDayjs}
                    localeText={{
                      clearButtonLabel: 'Empty',
                      todayButtonLabel: 'Now',
                    }}
                  >
                      <DesktopDatePicker
                        value={dataInicio ? dayjs(dataInicio) : null}
                        onChange={(value) => setDataInicio(value ? value.toDate() as Date : null)}
                        label={translate('dataInicio')}
                        format={formatoDataDayjs}
                        sx={{ width: '100%', mb: isMobile ? 1 : 0 }}
                        slotProps={{
                          textField: {
                            required: true,
                            placeholder: formatoDataDayjs,
                            fullWidth: true
                          },
                        }}
                      />
                    </LocalizationProvider>
                )}
                  </Grid>

                  <Grid item xs={12} md={4}>
                {periodo === 'RANGE_DATAS' && (
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={localeDayjs}
                    localeText={{
                      clearButtonLabel: 'Empty',
                      todayButtonLabel: 'Now',
                    }}
                  >
                      <DesktopDatePicker
                        value={dataFim ? dayjs(dataFim) : null}
                        onChange={(value) => setDataFim(value ? value.toDate() as Date : null)}
                        label={translate('dataFim')}
                        format={formatoDataDayjs}
                        sx={{ width: '100%' }}
                        slotProps={{
                          textField: {
                            required: true,
                            placeholder: formatoDataDayjs,
                            fullWidth: true
                          },
                        }}
                      />
                    </LocalizationProvider>
              )}
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', width: '100%', mt: isMobile ? 1 : 0 }}>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row', width: isMobile ? '100%' : 'auto' }}>
                  <ButtonStato
                    type="button"
                    variant="outlined"
                    onClick={filtrarRelatorio}
                    disabled={loading}
                    sx={{ width: isMobile ? '100%' : 'auto', mb: isMobile ? 1 : 0 }}
                  >
                    {loading && loadingAction === 'filtrar' ? <CircularProgress size={20} color="inherit" /> : translate('filtrar')}
                  </ButtonStato>
                  <ButtonStato
                    type="button"
                    variant="outlined"
                    onClick={gerarRelatorio}
                    disabled={loading}
                    sx={{ width: isMobile ? '100%' : 'auto' }}
                  >
                    {loading && loadingAction === 'exportar' ? <CircularProgress size={20} color="inherit" /> : translate('exportar')}
                  </ButtonStato>
                </Box>
              </Grid>
            </GridTimeSheet>
          </PaperRelatorio>
        </form>

        {(comandaEtapasReunioes.length > 0 || reunioesInternas.length > 0) && (
          <Box mt={0}>
            <TimesheetRestultado
              dataInicio={dataInicio || new Date()}
              dataFim={dataFim || new Date()}
              resultado={comandaEtapasReunioes}
              reunioesInternas={reunioesInternas}
              onRenderComplete={handleRenderComplete}
            />
          </Box>
        )}
      </DivConteudo>
    </>
  );
};

const GridTimeSheet = styled(Grid)`
  padding: ${themeConstants.spacing.sm}px ${themeConstants.spacing.md}px;
  min-height: 200px;
  display: flex;
  align-items: center;
  width: 100%;
`;

export const DivConteudo = styled(Box)`
  margin: 20px;
  width: 100%;
  overflow: hidden;
`;

export const PaperRelatorio = styled(Paper)`
  padding: ${themeConstants.spacing.md}px ${themeConstants.spacing.md}px;
  width: 100%;
  min-height: 200px;
  box-sizing: border-box;
  max-width: 100%;
`;

export default RelatorioTimeSheet;
